/**
 * ============================================================
 * HOBBLIVISION NAME EVIDENCE
 * ============================================================
 *
 * Converts noisy parser output into reliable identity evidence.
 *
 * Important principles:
 *
 *   1. Repeated OCR observations must remain meaningful.
 *   2. Parser-level identity fields such as `player` and
 *      `itemName` are strong evidence and should not be ignored.
 *   3. Multi-word names such as TY COBB and MICHAEL JORDAN
 *      should outrank OCR garbage.
 *   4. This utility produces retrieval/scoring evidence.
 *      It does NOT decide the final match.
 *
 * ============================================================
 */

const VERSION = "2.0";

const STOP_WORDS = new Set([
    "THE",
    "AND",
    "FOR",
    "CARD",
    "BASE",
    "ROOKIE",
    "RC",
    "MINT",
    "RETEN",
    "RENTEN",
    "GEM",
    "GEMMT",
    "MINT9",
    "MINT10",
    "PSA",
    "BGS",
    "SGC",
    "CGC",
    "BECKETT",
    "TOPPS",
    "UPPER",
    "DECK",
]);

const NOISE_TOKENS = new Set([
    "IF",
    "OF",
    "IN",
    "ON",
    "AT",
    "IS",
    "IT",
    "AS",
    "TO",
    "BY",
    "AN",
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
]);

function normalize(value) {
    return String(value || "")
        .toUpperCase()
        .replace(/[^\w\s'-]/g, " ")
        .replace(/[_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function sanitizeIdentityValue(value) {
    const cleaned = normalize(value).trim();

    if (!cleaned) {
        return "";
    }

    const tokens = cleaned.split(" ").filter(Boolean);

    if (tokens.length >= 2) {
        if (tokens[0].length === 1) {
            return tokens.slice(1).join(" ");
        }

        if (tokens[tokens.length - 1].length === 1) {
            return tokens.slice(0, -1).join(" ");
        }
    }

    return cleaned;
}

function extractCandidateText(candidate) {
    if (!candidate) return "";

    if (typeof candidate === "string") {
        return candidate;
    }

    return (
        candidate.value ||
        candidate.name ||
        candidate.text ||
        candidate.candidate ||
        candidate.raw ||
        ""
    );
}

/**
 * Return parser name candidates without deduplicating them.
 *
 * Repeated observations are valuable evidence.
 */
function extractCandidates(parsedOCR) {
    if (!parsedOCR) return [];

    const possibleSources = [
        parsedOCR.nameCandidates,
        parsedOCR.names,
        parsedOCR.nameEvidence,
        parsedOCR.candidates,
    ];

    for (const source of possibleSources) {
        if (Array.isArray(source) && source.length > 0) {
            return source;
        }
    }

    if (
        parsedOCR.card &&
        Array.isArray(parsedOCR.card.nameCandidates)
    ) {
        return parsedOCR.card.nameCandidates;
    }

    return [];
}

function cleanTokens(text) {
    return normalize(text)
        .split(" ")
        .filter(Boolean)
        .filter((token) => !STOP_WORDS.has(token))
        .filter((token) => !NOISE_TOKENS.has(token))
        .filter((token) => !/^\d+$/.test(token))
        .filter((token) => token.length >= 2);
}

/**
 * Calculate token frequency across actual observations.
 *
 * We intentionally do NOT deduplicate candidate strings here.
 */
function tokenFrequency(candidates) {
    const counts = new Map();

    for (const candidate of candidates) {
        const tokens = new Set(cleanTokens(candidate));

        for (const token of tokens) {
            counts.set(
                token,
                (counts.get(token) || 0) + 1
            );
        }
    }

    return counts;
}

/**
 * Build repeated 2- and 3-token phrase evidence.
 *
 * Example:
 *
 *   TY COBB
 *   TY COBB
 *
 * produces supportCount = 2.
 *
 * This is the critical fix for the Ty Cobb case.
 */
function buildPhraseEvidence(candidates) {
    const phraseMap = new Map();

    for (const candidate of candidates) {
        const tokens = cleanTokens(candidate);

        if (!tokens.length) continue;

        for (
            let size = 2;
            size <= Math.min(3, tokens.length);
            size++
        ) {
            for (
                let i = 0;
                i <= tokens.length - size;
                i++
            ) {
                const phrase = tokens
                    .slice(i, i + size)
                    .join(" ");

                const existing =
                    phraseMap.get(phrase) || {
                        value: phrase,
                        supportCount: 0,
                        observations: 0,
                    };

                existing.supportCount += 1;
                existing.observations += 1;

                phraseMap.set(phrase, existing);
            }
        }
    }

    return phraseMap;
}

/**
 * Add strong parser-level identity evidence.
 *
 * `player` and a clean `itemName` are much stronger than
 * arbitrary OCR-derived phrases.
 */
function addDirectIdentityEvidence(
    evidenceMap,
    parsedOCR
) {
    const directCandidates = [
        {
            value: parsedOCR?.player,
            confidence: parsedOCR?.playerConfidence,
            source: "player",
        },
        {
            value: parsedOCR?.itemName,
            confidence: parsedOCR?.itemNameConfidence,
            source: "itemName",
        },
    ];

    for (const candidate of directCandidates) {
        const value = sanitizeIdentityValue(candidate.value);

        if (!value) continue;

        const tokens = cleanTokens(value);

        /*
         * A useful identity should contain at least two meaningful
         * tokens. This prevents single OCR words such as SAVOY
         * from automatically becoming the primary identity.
         */
        if (tokens.length < 2) continue;

        const parserConfidence =
            Number.isFinite(candidate.confidence)
                ? candidate.confidence
                : 0.75;

        const existing = evidenceMap.get(value);

        const evidence = {
            value,
            supportCount: Math.max(
                existing?.supportCount || 0,
                1
            ),
            observations: Math.max(
                existing?.observations || 0,
                1
            ),
            confidence: Math.max(
                existing?.confidence || 0,
                Math.min(1, parserConfidence)
            ),
            source: candidate.source,
            directIdentity: true,
        };

        evidenceMap.set(value, evidence);
    }
}

function scorePhraseEvidence(
    evidence,
    totalObservations,
    tokenCounts
) {
    const supportRatio =
        totalObservations > 0
            ? evidence.supportCount /
              totalObservations
            : 0;

    const tokens = evidence.value.split(" ");

    const tokenSupport =
        tokens.reduce(
            (sum, token) =>
                sum + (tokenCounts.get(token) || 0),
            0
        ) /
        Math.max(
            tokens.length * totalObservations,
            1
        );

    let confidence =
        supportRatio * 0.65 +
        tokenSupport * 0.35;

    if (tokens.length >= 2) {
        confidence += 0.10;
    }

    if (evidence.supportCount >= 2) {
        confidence += 0.08;
    }

    if (evidence.supportCount >= 3) {
        confidence += 0.05;
    }

    return Math.min(0.99, confidence);
}

/**
 * Analyze parser output and return ranked identity evidence.
 */
export function extractNameEvidence(
    parsedOCR,
    options = {}
) {
    const rawCandidates = extractCandidates(parsedOCR)
        .map(extractCandidateText)
        .map(sanitizeIdentityValue)
        .filter(Boolean);

    const observations = rawCandidates.length;

    if (!observations) {
        /*
         * Even if the parser supplied no nameCandidates array,
         * direct identity fields may still be usable.
         */
        const directOnly = new Map();

        addDirectIdentityEvidence(
            directOnly,
            parsedOCR
        );

        const directEvidence = [...directOnly.values()];

        return {
            version: VERSION,
            evidence: directEvidence,
            primary: directEvidence[0] || null,
            observations: 0,
        };
    }

    const tokenCounts =
        tokenFrequency(rawCandidates);

    const phraseMap =
        buildPhraseEvidence(rawCandidates);

    const evidenceMap = new Map();

    /*
     * First add phrase-derived evidence.
     */
    for (const item of phraseMap.values()) {
        const confidence =
            scorePhraseEvidence(
                item,
                observations,
                tokenCounts
            );

        if (confidence < 0.30) continue;

        evidenceMap.set(item.value, {
            ...item,
            confidence,
            source: "ocr_phrase",
            directIdentity: false,
        });
    }

    /*
     * Then add direct parser identity.
     *
     * This is deliberately done AFTER phrase extraction so
     * direct identity can override weaker OCR garbage.
     */
    addDirectIdentityEvidence(
        evidenceMap,
        parsedOCR
    );

    const evidence = [...evidenceMap.values()]
        .sort((a, b) => {
            /*
             * Direct parser identity gets priority when
             * confidence is reasonably strong.
             */
            if (
                a.directIdentity !==
                b.directIdentity
            ) {
                return a.directIdentity ? -1 : 1;
            }

            if (b.confidence !== a.confidence) {
                return b.confidence - a.confidence;
            }

            if (
                b.supportCount !==
                a.supportCount
            ) {
                return (
                    b.supportCount -
                    a.supportCount
                );
            }

            return (
                b.value.length -
                a.value.length
            );
        });

    /*
     * Prefer a strong direct identity.
     *
     * Otherwise choose repeated meaningful phrase evidence.
     */
    const primary =
        evidence.find(
            (item) =>
                item.directIdentity &&
                item.confidence >= 0.70
        ) ||
        evidence.find(
            (item) =>
                item.supportCount >= 2 &&
                item.confidence >= 0.45
        ) ||
        evidence[0] ||
        null;

    return {
        version: VERSION,
        evidence,
        primary,
        observations,
    };
}

export default extractNameEvidence;